import localforage from 'localforage'
import type { Query, DocumentData } from 'firebase/firestore'
import {
  getFirebaseDb,
  pathDoc,
  pathCollection,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp
} from '@/infra/firebase/firestore'
import { emitter } from '@/plugins/emitter'
import _ from '@/assets/lib/my-lodash'
import {
  BACKUP_KEYS_LIST,
  BACKUP_SAVE_LIMIT_NUM,
  DEFAULT_QUERY_LIMIT_NUM,
  LAST_SAVE_DATE_SAVE_KEY,
  SITE_ID,
  AUTO_BACKUP_INTERVAL_MS
} from './constants'

/** Backup / Firestore query helpers bound onto the $firebase Vue host */
export const backupMethods = {
  createCollectionQuery(
    this: any,
    path: string,
    limitNum?: number
  ): Query<DocumentData> {
    const lim = limitNum || DEFAULT_QUERY_LIMIT_NUM
    const db = getFirebaseDb()
    return query(
      pathCollection(db, path),
      where('uid', '==', this.user.uid),
      where('site', '==', SITE_ID),
      orderBy('timestamp', 'desc'),
      limit(lim)
    )
  },

  createUnlimitedCollectionQuery(this: any, path: string): Query<DocumentData> {
    const db = getFirebaseDb()
    return query(
      pathCollection(db, path),
      where('uid', '==', this.user.uid),
      where('site', '==', SITE_ID),
      orderBy('timestamp', 'desc')
    )
  },

  createCollectionQuery4Admin(
    this: any,
    _path: string,
    _limitNum?: number
  ): Query<DocumentData> {
    return query(pathCollection(getFirebaseDb(), 'userdata'), limit(10))
  },

  updateUserDocTimestamp(this: any) {
    const userDocRef = pathDoc(getFirebaseDb(), this.firestorePaths.userDoc)
    setDoc(
      userDocRef,
      {
        timestamp: serverTimestamp()
      },
      {
        merge: true
      }
    )
  },

  loadBackup(this: any, id: string) {
    const errors: unknown[] = []
    const app = this
    getDoc(pathDoc(getFirebaseDb(), this.firestorePaths.backups, id))
      .then(function (documentSnapshot) {
        if (!documentSnapshot.exists()) {
          app.$message({
            message: 'バックアップデータが見つかりませんでした',
            type: 'error'
          })
          return
        }
        const data = JSON.parse(documentSnapshot.data()!.content)
        _.forEach(BACKUP_KEYS_LIST, function (key: string) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            const dataForLocal = data[key]
            localforage.setItem(key, dataForLocal, function (err: unknown) {
              if (err) {
                console.error(err)
                errors.push(err)
                return
              }
              console.log('backup-loaded:', key, dataForLocal)
              emitter.emit('backup-loaded-' + key, dataForLocal)
            })
          }
        })
        if (errors.length === 0) {
          app.$message({
            message:
              'バックアップを読み込みました:' + documentSnapshot.data()!.label,
            type: 'success'
          })
        }
      })
      .catch(function (error: unknown) {
        app.$message({
          message: 'バックアップの読み込みに失敗しました',
          type: 'error'
        })
        console.error(error)
      })
  },

  deleteBackup(this: any, id: string) {
    return deleteDoc(pathDoc(getFirebaseDb(), this.firestorePaths.backups, id))
      .then(function () {
        console.log('Document successfully deleted!')
        return true
      })
      .catch(function (error: unknown) {
        console.error('Error removing document: ', error)
        return false
      })
  },

  deleteBackupWithMessage(this: any, id: string, label: string) {
    const app = this
    deleteDoc(pathDoc(getFirebaseDb(), this.firestorePaths.backups, id))
      .then(function () {
        app.$message({
          message: label + 'を削除しました'
        })
        return true
      })
      .catch(function (error: unknown) {
        app.$message({
          message: 'バックアップの削除に失敗しました',
          type: 'error'
        })
        console.error('Error removing document: ', error)
        return false
      })
  },

  loadLastBackupSaveDate(this: any) {
    const app = this
    const q = this.createCollectionQuery(this.firestorePaths.backups, 1)
    return getDocs(q)
      .then(function (querySnapshot) {
        querySnapshot.forEach(function (docSnap) {
          if (!docSnap.exists()) {
            console.log('no backup exists')
            throw new Error('noBackup')
          }
          const data = docSnap.data() as {
            lastSaveDate?: { toDate?: () => Date } | string | Date
          }
          if (!data.lastSaveDate) {
            return
          }
          if (
            typeof data.lastSaveDate === 'object' &&
            data.lastSaveDate !== null &&
            typeof (data.lastSaveDate as { toDate?: () => Date }).toDate !==
              'undefined'
          ) {
            app.lastBackupSaveDate = (
              data.lastSaveDate as { toDate: () => Date }
            ).toDate()
          } else {
            app.lastBackupSaveDate = new Date(
              data.lastSaveDate as string | Date
            )
          }
        })
      })
      .catch(function (e: unknown) {
        console.error(e)
        throw new Error('queryError')
      })
  },

  loadLastLocalSaveDate(this: any) {
    const app = this
    return localforage.getItem(LAST_SAVE_DATE_SAVE_KEY).then(function (
      data: any
    ) {
      if (!data) {
        throw new Error('noLocalData')
      }
      try {
        app.lastLocalSaveDate = new Date(data)
      } catch (e) {
        app.lastLocalSaveDate = data
      }
    })
  },

  async needAddingBackup(this: any) {
    if (!this.isAuthenticated) {
      return false
    }
    try {
      await this.loadLastBackupSaveDate()
      await this.loadLastLocalSaveDate()
    } catch (e: any) {
      if (e.message === 'noLocalData') {
        return false
      }
      if (e.message === 'noBackup') {
        return true
      }
      console.error('un expected error on load LastSaveDates', e)
      return false
    }
    if (this.lastLocalSaveDate > this.lastBackupSaveDate) {
      return true
    } else {
      return false
    }
  },

  async alertIfNeedsLoadingBackup(this: any) {
    if (!this.isAuthenticated) {
      return false
    }
    let errorMessage = ''
    try {
      await this.loadLastBackupSaveDate()
      await this.loadLastLocalSaveDate()
    } catch (e: any) {
      errorMessage = e.message
      if (errorMessage === 'noBackup') {
        return
      }
    }
    if (!this.lastBackupSaveDate) {
      return
    }
    if (errorMessage === 'noLocalData' && this.lastBackupSaveDate.getTime()) {
      return
    }
    if (this.lastLocalSaveDate.getTime() >= this.lastBackupSaveDate.getTime()) {
      console.log('データは最新です')
      return
    }
    if (this.lastLocalSaveDate < this.lastBackupSaveDate) {
      emitter.emit('yagi-message', {
        text: 'サーバーに新しいバックアップがあるよ！',
        durationMs: 5000
      })
    }
  },

  getBackupList(this: any, type: string, limitNum?: number) {
    if (!this.isAuthenticated) {
      console.log('getBackupList !this.isAuthenticated')
      return
    }
    const db = getFirebaseDb()
    const lim = limitNum || DEFAULT_QUERY_LIMIT_NUM
    let q
    if (type === 'user' || type === 'auto') {
      q = query(
        pathCollection(db, this.firestorePaths.backups),
        where('uid', '==', this.user.uid),
        where('site', '==', SITE_ID),
        where('type', '==', type),
        orderBy('timestamp', 'desc'),
        limit(lim)
      )
    } else {
      q = this.createCollectionQuery(this.firestorePaths.backups, lim)
    }
    return getDocs(q)
  },

  getBackupList4Admin(this: any, type: string, limitNum?: number) {
    const app = this
    if (!this.isAuthenticated) {
      console.log('getBackupList4Admin !this.isAuthenticated')
      return
    }
    let membercount = 0
    let backupcount = 0
    console.log('111')
    const q = this.createCollectionQuery4Admin(
      this.firestorePaths.backups,
      limitNum
    )
    getDocs(q)
      .then(function (querySnapshot) {
        querySnapshot.forEach(function (docSnap) {
          membercount++
          console.log(`membercount/${membercount}/`)
          const userdataPath = `userdata/${docSnap.id}/backups`
          const subQuery = app.createUnlimitedCollectionQuery(userdataPath)
          getDocs(subQuery)
            .then(function (subQuerySnapshot) {
              subQuerySnapshot.forEach(function (_doc) {
                console.log('XX')
                backupcount++
                console.log(`backupcount/${backupcount}/`)
              })
            })
            .catch(function (err: unknown) {
              console.log('141414')
              console.error(err)
            })
        })
      })
      .catch(function (err: unknown) {
        console.log('444')
        console.error(err)
      })
    return getDocs(q)
  },

  deleteOverLimitBackups(this: any) {
    if (!this.isAuthenticated) {
      return
    }
    const app = this
    const q = this.createCollectionQuery(this.firestorePaths.backups, 100)
    getDocs(q)
      .then(function (querySnapshot) {
        let count = 0
        querySnapshot.forEach(function (docSnap) {
          const docData = docSnap.data() as { type?: string }
          if (count > BACKUP_SAVE_LIMIT_NUM) {
            if (docData.type === 'auto') {
              app.deleteBackup(docSnap.id)
              console.log('auto backup deleted', docSnap.id)
            }
          }
          count++
        })
      })
      .catch(function (e: unknown) {
        console.error(e)
        throw new Error('queryError')
      })
  },

  async getYagishLightData(this: any, id: string) {
    this.setupDb()
    const docSnap = await getDoc(pathDoc(getFirebaseDb(), 'line_json_data', id))
    return docSnap.data()
  },

  async autoBackup(this: any) {
    if (!this.isAuthenticated) return
    console.log('autobackup skip, autoSave is false')
  },

  activateAutoBackup(this: any) {
    if (!this.autoBackupTimer) {
      this.autoBackupTimer = setInterval(
        this.autoBackup,
        AUTO_BACKUP_INTERVAL_MS
      )
    } else {
      console.log('autoBackupTimer already exists. skip')
    }
  },

  deactivateAutoBackup(this: any) {
    clearInterval(this.autoBackupTimer)
  }
}
